import React, { useState, useEffect, useCallback } from 'react';
import { FiEye, FiTrash2, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, DataTable, Button, Badge, ConfirmDialog, Modal } from '../../components/common';
import { careerApplicationService } from '../../services/careerApplicationService';
import { formatDate } from '../../utils/dateUtils';
import { truncate } from '../../utils/stringUtils';
import { useDebounce, useModal } from '../../hooks';
import './CareerApplicationsPage.css';

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'pending',     label: 'Pending' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'rejected',    label: 'Rejected' },
  { value: 'hired',       label: 'Hired' },
];

const statusVariants = {
  pending:     'warning',
  shortlisted: 'info',
  rejected:    'danger',
  hired:       'success',
};

// ─── Component ────────────────────────────────────────────────────────────────

const CareerApplicationsPage = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [currentPage, setCurrentPage]   = useState(1);
  const [totalItems, setTotalItems]     = useState(0);
  const [statusLoading, setStatusLoading] = useState(null); // id being updated

  const detailModal = useModal();
  const deleteModal = useModal();
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Pending status change inside the detail modal
  const [pendingStatus, setPendingStatus] = useState('');

  const debouncedSearch = useDebounce(search, 300);
  const pageSize = 10;

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await careerApplicationService.getAll({
        page:   currentPage,
        limit:  pageSize,
        search: debouncedSearch,
      });
      setApplications(response.data.applications);
      setTotalItems(response.data.total);
    } catch {
      toast.error('Failed to fetch career applications');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  // ── Open detail modal ──────────────────────────────────────────────────────

  const handleViewApplication = (app) => {
    setPendingStatus(app.status);
    detailModal.open(app);
  };

  // ── Inline status update (from table dropdown) ─────────────────────────────

  const handleInlineStatusChange = async (id, newStatus) => {
    setStatusLoading(id);
    try {
      await careerApplicationService.updateStatus(id, newStatus);
      setApplications((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a))
      );
      toast.success('Application status updated');
    } catch {
      toast.error('Failed to update status');
    } finally {
      setStatusLoading(null);
    }
  };

  // ── Status update from detail modal ───────────────────────────────────────

  const handleModalStatusSave = async () => {
    if (!detailModal.data) return;
    const { id } = detailModal.data;
    if (pendingStatus === detailModal.data.status) {
      detailModal.close();
      return;
    }
    setStatusLoading(id);
    try {
      await careerApplicationService.updateStatus(id, pendingStatus);
      setApplications((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: pendingStatus } : a))
      );
      toast.success('Application status updated');
      detailModal.close();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setStatusLoading(null);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await careerApplicationService.delete(deleteModal.data);
      toast.success('Application deleted successfully');
      fetchApplications();
    } catch {
      toast.error('Failed to delete application');
    } finally {
      setDeleteLoading(false);
      deleteModal.close();
    }
  };

  // ── Columns ────────────────────────────────────────────────────────────────

  const columns = [
    {
      header: 'Applicant',
      render: (row) => (
        <div className="applicant-cell">
          <div className="applicant-avatar">
            {(row.fullName || '?').charAt(0)}
          </div>
          <div className="applicant-info">
            <span className="applicant-name">{row.fullName || '—'}</span>
            <span className="applicant-email">{row.email}</span>
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      render: (row) => (
        <div className="position-cell">
          <span className="position-title">
            {truncate(row.careerTitle || `Job #${row.careerId}`, 45)}
          </span>
          {row.phone && (
            <span className="position-phone">{row.phone}</span>
          )}
        </div>
      ),
    },
    {
      header: 'Status',
      width: '145px',
      render: (row) => (
        <select
          className="status-select"
          value={row.status}
          disabled={statusLoading === row.id}
          onChange={(e) => handleInlineStatusChange(row.id, e.target.value)}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ),
    },
    {
      header: 'Label',
      width: '110px',
      render: (row) => (
        <Badge variant={statusVariants[row.status] || 'secondary'}>
          {STATUS_OPTIONS.find((o) => o.value === row.status)?.label || row.status}
        </Badge>
      ),
    },
    {
      header: 'Applied On',
      width: '120px',
      render: (row) => (
        <span className="applied-date">{formatDate(row.appliedAt)}</span>
      ),
    },
    {
      header: 'Actions',
      width: '100px',
      align: 'center',
      render: (row) => (
        <div className="action-buttons">
          <button
            className="action-btn view"
            title="View Application"
            onClick={() => handleViewApplication(row)}
          >
            <FiEye />
          </button>
          <button
            className="action-btn delete"
            title="Delete"
            onClick={() => deleteModal.open(row.id)}
          >
            <FiTrash2 />
          </button>
        </div>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="career-applications-page">
      <Card
        title="Career Applications"
        subtitle={`${totalItems} application${totalItems !== 1 ? 's' : ''}`}
      >
        <DataTable
          columns={columns}
          data={applications}
          loading={loading}
          searchable
          searchPlaceholder="Search by name, email or position…"
          searchValue={search}
          onSearch={handleSearch}
          pagination
          pageSize={pageSize}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          exportable
          exportFilename="career-applications-export"
          emptyMessage="No applications received yet"
        />
      </Card>

      {/* ── Detail / Status Modal ─────────────────────────────────────── */}
      <Modal
        isOpen={detailModal.isOpen}
        onClose={detailModal.close}
        title="Application Details"
        size="medium"
        footer={
          <>
            <Button variant="ghost" onClick={detailModal.close}>
              Close
            </Button>
            <Button
              onClick={handleModalStatusSave}
              loading={statusLoading === detailModal.data?.id}
            >
              Save Status
            </Button>
          </>
        }
      >
        {detailModal.data && (
          <div className="application-detail">

            {/* Applicant Info */}
            <div className="detail-section">
              <div className="detail-section-title">Applicant Information</div>
              <div className="detail-grid">
                <div className="detail-field">
                  <span className="detail-label">Full Name</span>
                  <span className="detail-value">{detailModal.data.fullName || '—'}</span>
                </div>
                <div className="detail-field">
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">{detailModal.data.phone || '—'}</span>
                </div>
                <div className="detail-field">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{detailModal.data.email || '—'}</span>
                </div>
                <div className="detail-field">
                  <span className="detail-label">Applied On</span>
                  <span className="detail-value">
                    {formatDate(detailModal.data.appliedAt)}
                  </span>
                </div>
              </div>
            </div>

            {/* Position Info */}
            <div className="detail-section">
              <div className="detail-section-title">Position</div>
              <div className="detail-grid">
                <div className="detail-field">
                  <span className="detail-label">Position Applied For</span>
                  <span className="detail-value">
                    {detailModal.data.careerTitle || `Job #${detailModal.data.careerId}` || '—'}
                  </span>
                </div>
                {detailModal.data.resumeUrl && (
                  <div className="detail-field">
                    <span className="detail-label">Resume</span>
                    <span className="detail-value">
                      <a
                        href={detailModal.data.resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FiFileText style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        View / Download Resume
                      </a>
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Cover Letter */}
            {detailModal.data.coverLetter && (
              <div className="detail-section">
                <div className="detail-section-title">Cover Letter</div>
                <p className="cover-letter-text">{detailModal.data.coverLetter}</p>
              </div>
            )}

            {/* Status Update */}
            <div className="detail-section">
              <div className="detail-section-title">Update Status</div>
              <div className="status-update-row">
                <span className="status-update-label">Current Status:</span>
                <Badge variant={statusVariants[detailModal.data.status] || 'secondary'}>
                  {STATUS_OPTIONS.find((o) => o.value === detailModal.data.status)?.label
                    || detailModal.data.status}
                </Badge>
                <select
                  className="status-select-modal"
                  value={pendingStatus}
                  onChange={(e) => setPendingStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

          </div>
        )}
      </Modal>

      {/* ── Delete Confirmation ─────────────────────────────────────────── */}
      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        title="Delete Application"
        message="Are you sure you want to delete this application? This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default CareerApplicationsPage;
